import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { LIBRARY_IMAGE_LISTING_FIELDS } from '../../util/listingLibraryImages';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  getLastTransitionsForInboxStatus,
  isTransactionUuid,
  transactionMatchesInboxSearch,
} from './inboxFilters';

const { UUID } = sdkTypes;

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;
const INBOX_SEARCH_PAGE_SIZE = 100;
/** Cap how many API pages text search will scan (100 × N transactions). */
const INBOX_SEARCH_MAX_PAGES = 20;

const inboxIncludeParams = {
  include: [
    'listing',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'booking',
  ],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
  ],
  'fields.listing': [
    'title',
    'availabilityPlan',
    'publicData.listingType',
    'publicData.eventName',
    'publicData.event_name',
    ...LIBRARY_IMAGE_LISTING_FIELDS,
  ],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

const participantId = (tx, role) =>
  tx?.[role]?.id?.uuid || tx?.relationships?.[role]?.data?.id?.uuid || null;

const transactionMatchesOnlyFilter = (tx, onlyFilter, currentUserId) => {
  if (!tx || !currentUserId) {
    return false;
  }
  if (onlyFilter === 'order') {
    return participantId(tx, 'customer') === currentUserId;
  }
  if (onlyFilter === 'sale') {
    return participantId(tx, 'provider') === currentUserId;
  }
  return false;
};

const emptySearchResponse = () => ({
  data: {
    data: [],
    meta: { totalItems: 0, totalPages: 1, page: 1, perPage: 1 },
  },
});

const searchResultResponse = (txs, included = []) => ({
  data: {
    data: txs,
    included,
    meta: {
      totalItems: txs.length,
      totalPages: 1,
      page: 1,
      perPage: Math.max(txs.length, 1),
    },
  },
});

/**
 * Fetch up to maxPages of transactions.query results, merging data + included.
 */
const queryTransactionsPages = async (sdk, apiQueryParams, { maxPages = 1 } = {}) => {
  let page = 1;
  let totalPages = 1;
  const allTxs = [];
  const includedByKey = new Map();
  let lastResponse = null;

  while (page <= totalPages && page <= maxPages) {
    const response = await sdk.transactions.query({
      ...apiQueryParams,
      page,
    });
    lastResponse = response;
    (response.data.data || []).forEach(tx => allTxs.push(tx));
    (response.data.included || []).forEach(entity => {
      const key = `${entity.type}:${entity.id?.uuid || entity.id}`;
      if (!includedByKey.has(key)) {
        includedByKey.set(key, entity);
      }
    });
    totalPages = response.data.meta?.totalPages || 1;
    page += 1;
  }

  if (!lastResponse) {
    return emptySearchResponse();
  }

  return {
    ...lastResponse,
    data: {
      ...lastResponse.data,
      data: allTxs,
      included: Array.from(includedByKey.values()),
      meta: {
        totalItems: allTxs.length,
        totalPages: 1,
        page: 1,
        perPage: Math.max(allTxs.length, 1),
      },
    },
  };
};

const dispatchSearchSuccess = (dispatch, response) => {
  dispatch(addMarketplaceEntities(response));
  dispatch(fetchOrdersOrSalesSuccess(response));
  return response;
};

/**
 * Text search: page through sales/orders and keep client-side matches.
 * Sharetribe has no keyword filter on transactions.query.
 */
const loadTextSearchResults = async ({
  dispatch,
  getState,
  sdk,
  onlyFilter,
  lastTransitions,
  searchQuery,
}) => {
  const isOrders = onlyFilter === 'order';
  const matchedTxs = [];
  const includedByKey = new Map();
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages && page <= INBOX_SEARCH_MAX_PAGES) {
    const response = await sdk.transactions.query({
      only: onlyFilter,
      lastTransitions,
      ...inboxIncludeParams,
      page,
      perPage: INBOX_SEARCH_PAGE_SIZE,
    });

    dispatch(addMarketplaceEntities(response));

    const pageTxs = response.data.data || [];
    const denormalized = getMarketplaceEntities(getState(), entityRefs(pageTxs));

    pageTxs.forEach((rawTx, index) => {
      const tx = denormalized[index];
      if (tx && transactionMatchesInboxSearch(tx, searchQuery, { isOrders })) {
        matchedTxs.push(rawTx);
      }
    });

    (response.data.included || []).forEach(entity => {
      const key = `${entity.type}:${entity.id?.uuid || entity.id}`;
      if (!includedByKey.has(key)) {
        includedByKey.set(key, entity);
      }
    });

    totalPages = response.data.meta?.totalPages || 1;
    page += 1;
  }

  const wrapped = searchResultResponse(matchedTxs, Array.from(includedByKey.values()));
  dispatch(fetchOrdersOrSalesSuccess(wrapped));
  return wrapped;
};

/**
 * UUID search: try as transaction id, then as listing id.
 * @param {string[]|null} statusTransitions - lastTransitions for an active status
 *   filter, or null when "All statuses" (do not restrict listingId fallback).
 */
const loadUuidSearchResults = ({
  dispatch,
  getState,
  sdk,
  onlyFilter,
  statusTransitions,
  searchQuery,
}) => {
  const uuid = new UUID(searchQuery);

  return sdk.transactions
    .show({
      id: uuid,
      ...inboxIncludeParams,
    })
    .then(response => {
      // JSON:API payload is not denormalized — provider/customer live under relationships.
      const [denormTx] = denormalisedResponseEntities(response);
      const rawTx = response.data.data;
      const currentUserId = getState()?.user?.currentUser?.id?.uuid;
      // If currentUser isn't in Redux yet, still show when show() succeeded —
      // Marketplace API only returns txs the authenticated user participates in.
      const allowed = currentUserId
        ? transactionMatchesOnlyFilter(denormTx || rawTx, onlyFilter, currentUserId)
        : true;
      const data = allowed ? [rawTx] : [];
      const wrapped = {
        ...response,
        data: {
          ...response.data,
          data,
          meta: {
            totalItems: data.length,
            totalPages: 1,
            page: 1,
            perPage: 1,
          },
        },
      };
      if (allowed) {
        dispatch(addMarketplaceEntities(response));
      }
      dispatch(fetchOrdersOrSalesSuccess(wrapped));
      return wrapped;
    })
    .catch(e => {
      if (e?.status !== 404 && e?.status !== 403) {
        dispatch(fetchOrdersOrSalesError(storableError(e)));
        throw e;
      }

      // Not a transaction id (or forbidden) — treat UUID as a listing id.
      // Only apply lastTransitions when a status filter is selected.
      const listingQuery = {
        only: onlyFilter,
        listingId: searchQuery,
        ...inboxIncludeParams,
        perPage: INBOX_SEARCH_PAGE_SIZE,
        ...(statusTransitions?.length ? { lastTransitions: statusTransitions } : {}),
      };

      return queryTransactionsPages(sdk, listingQuery, { maxPages: INBOX_SEARCH_MAX_PAGES })
        .then(response => dispatchSearchSuccess(dispatch, response))
        .catch(listingErr => {
          dispatch(fetchOrdersOrSalesError(storableError(listingErr)));
          throw listingErr;
        });
    });
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1, status, q } = parse(search);
  const searchQuery = typeof q === 'string' ? q.trim() : '';
  const statusTransitions = getLastTransitionsForInboxStatus(status);
  const lastTransitions = statusTransitions || getAllTransitionsForEveryProcess();

  if (isTransactionUuid(searchQuery)) {
    return loadUuidSearchResults({
      dispatch,
      getState,
      sdk,
      onlyFilter,
      statusTransitions,
      searchQuery,
    });
  }

  if (searchQuery.length > 0) {
    return loadTextSearchResults({
      dispatch,
      getState,
      sdk,
      onlyFilter,
      lastTransitions,
      searchQuery,
    }).catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
  }

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions,
    ...inboxIncludeParams,
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
